/**
 * MARKETING COMPASS — ACCESS CHECK (client side)
 * ──────────────────────────────────────────────
 * Marketing Compass is included in the paid tier at no extra charge. It is
 * gated on the EXISTING licence scopes: a `roadmap` or `bundle` licence grants
 * it. No new scope is introduced.
 *
 * ── Why there is no `authorId` parameter ──
 * The build spec called for `hasMarketingAccess(authorId)`. The licence model
 * doesn't work that way: `licenses` docs are keyed by `claimedBy` (an auth UID)
 * and by verified purchase email — never by authorId. So access is keyed by the
 * signed-in UID, exactly like `getMyLicence`, and the UID travels implicitly in
 * the auth token rather than as a spoofable argument.
 *
 * ── Why this calls a callable instead of reading Firestore ──
 * `firestore.rules` denies all client reads of `licenses` (superadmin-read only,
 * `allow write: if false`). Entitlement is resolved server-side by `getMyLicence`
 * via the Admin SDK. This helper is therefore a thin wrapper, not a second
 * source of truth.
 *
 * ── What this deliberately does NOT trust ──
 * `useLicence` grants a synthetic `bundle` licence to authors whose
 * `authorSubscription.tier` is pro/publisher/superadmin. That tier lives in
 * `authors/{authorId}/settings/subscription`, which `firestore.rules` lets the
 * AUTHOR THEMSELVES write. Trusting it here would let any verified author grant
 * themselves the paid tier. This helper never reads it, and the server-side
 * check that guards `getMarketingRecommendation` (Phase 5) must not either.
 * See docs/author-hub-discovery-report.md §3.
 */
import { getMyLicenceFn } from '../licensing/licenceApi';

/**
 * Which licence product entitles Marketing Compass.
 *
 * `getMyLicence` filters by `licenceEntitles`, under which asking for 'roadmap'
 * matches scope 'roadmap' OR 'bundle' (and legacy scope-less licences, which are
 * treated as roadmap). That is precisely the intended gate.
 *
 * Switching to a dedicated `marketing` scope later is a one-line change here —
 * which is the whole point of routing every check through this module.
 */
export const MARKETING_ACCESS_PRODUCT = 'roadmap';

/** Access states, in the order the UI cares about. */
export const ACCESS = {
  LOADING: 'loading',
  GRANTED: 'granted',
  EXPIRED: 'expired',        // had a licence, it lapsed → renew CTA
  NONE: 'none',              // never had one → "included with your plan" CTA
  SIGNED_OUT: 'signed_out',
  ERROR: 'error',            // network/callable failure → fail closed, retryable
};

/**
 * Resolve the signed-in user's Marketing Compass access.
 *
 * Fails CLOSED: any callable error yields ACCESS.ERROR, never GRANTED. The UI
 * shows a retry, and the Phase 5 server-side check re-verifies independently —
 * a client that lies about this gets nothing.
 *
 * @param {object|null} user  the Firebase auth user (null when signed out)
 * @returns {Promise<{state: string, licence: object|null}>}
 */
export async function hasMarketingAccess(user) {
  if (!user) return { state: ACCESS.SIGNED_OUT, licence: null };

  try {
    const res = await getMyLicenceFn({ product: MARKETING_ACCESS_PRODUCT });
    const lic = res?.data;

    if (!lic?.found) return { state: ACCESS.NONE, licence: null };
    if (lic.expired) return { state: ACCESS.EXPIRED, licence: lic.licence || null };
    return { state: ACCESS.GRANTED, licence: lic.licence || null };
  } catch (err) {
    console.error('[hubAccess] getMyLicence failed:', err?.code, err?.message);
    return { state: ACCESS.ERROR, licence: null };
  }
}

/** Convenience predicate for render guards. */
export const isUnlocked = (state) => state === ACCESS.GRANTED;
